/**
 * Message references: the listing, order, or bundle a single chat message is
 * about. One peer conversation belongs to one pair of people, so the item
 * travels with the message as `metadata.sharedContext`; the inbox reports the
 * newest one per thread as `latestReference`.
 *
 * The wire shape and the plain-text fallback mirror the mobile app
 * (`SharedMessageContext` in markitmix-mobile) and the backend
 * (`backend/src/modules/conversations/references.ts`). A message body sent
 * with a card is prefixed with `referenceSummary()` so clients that predate
 * cards still read the context; every card-aware client strips it again.
 */

export type MessageReferenceKind =
  | 'listing'
  | 'shipping_order'
  | 'pickup_order'
  | 'shipping_bundle'
  | 'pickup_bundle'

export interface MessageReference {
  version?: number
  kind: MessageReferenceKind
  entityId: string
  displayTitle?: string | null
  /** Saved as the sender wrote it; the wire summary is rebuilt from it byte for byte. */
  reference?: string | null
  /** An order card's complete order number, attached by the server. Shown instead of `reference`. */
  orderNumber?: string | null
  status?: string | null
  listingId?: string | null
  orderCount?: number
  itemCount?: number
  totalAmount?: number | null
  shippingAmount?: number | null
  thumbnailPaths?: string[]
}

/** A reference plus which message carried it (inbox rows). */
export interface LatestMessageReference extends MessageReference {
  messageId: string
  createdAt: string
}

const KINDS: ReadonlySet<string> = new Set<MessageReferenceKind>([
  'listing',
  'shipping_order',
  'pickup_order',
  'shipping_bundle',
  'pickup_bundle',
])

const LEGACY_BUNDLE_KINDS: Record<string, MessageReferenceKind> = {
  smart_bundle: 'shipping_bundle',
  shipping_bundle: 'shipping_bundle',
  pickup_bundle: 'pickup_bundle',
}

function text(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed ? trimmed : undefined
}

function num(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') return undefined
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : undefined
}

function count(value: unknown): number {
  const n = num(value)
  return n !== undefined && n >= 1 ? Math.round(n) : 1
}

function paths(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  const out: string[] = []
  for (const raw of value) {
    const p = text(raw)
    if (p && !out.includes(p)) out.push(p)
  }
  return out
}

export function isListingReference(ref: MessageReference): boolean {
  return ref.kind === 'listing'
}

export function isOrderReference(ref: MessageReference): boolean {
  return ref.kind === 'shipping_order' || ref.kind === 'pickup_order'
}

export function isBundleReference(ref: MessageReference): boolean {
  return ref.kind === 'shipping_bundle' || ref.kind === 'pickup_bundle'
}

export function isPickupReference(ref: MessageReference): boolean {
  return ref.kind === 'pickup_order' || ref.kind === 'pickup_bundle'
}

/** Read the card a message carries, or null. Accepts the legacy bundle payload. */
export function parseMessageReference(
  metadata: Record<string, unknown> | null | undefined
): MessageReference | null {
  if (!metadata || typeof metadata !== 'object') return null

  const shared = metadata.sharedContext
  if (shared && typeof shared === 'object') {
    const s = shared as Record<string, unknown>
    const kind = text(s.kind)
    const entityId = text(s.entityId)
    if (kind && KINDS.has(kind) && entityId) {
      return {
        kind: kind as MessageReferenceKind,
        entityId,
        displayTitle: text(s.displayTitle) ?? null,
        reference: text(s.reference) ?? null,
        orderNumber: text(s.orderNumber) ?? null,
        status: text(s.status) ?? null,
        listingId: text(s.listingId) ?? null,
        orderCount: count(s.orderCount),
        itemCount: count(s.itemCount),
        totalAmount: num(s.totalAmount) ?? null,
        shippingAmount: num(s.shippingAmount) ?? null,
        thumbnailPaths: paths(s.thumbnailPaths),
      }
    }
  }

  const bundle = metadata.bundleContext
  if (bundle && typeof bundle === 'object') {
    const b = bundle as Record<string, unknown>
    const kind = LEGACY_BUNDLE_KINDS[text(b.kind) ?? '']
    const entityId = text(b.bundleId)
    if (kind && entityId) {
      const isPickup = kind === 'pickup_bundle'
      const amount = num(b.amount)
      const many = paths(b.thumbnailPaths)
      const single = text(b.thumbnailPath)
      return {
        kind,
        entityId,
        orderCount: count(b.orderCount),
        itemCount: count(b.itemCount),
        totalAmount: num(b.orderTotal) ?? (isPickup ? amount : undefined) ?? null,
        shippingAmount: isPickup ? null : amount ?? null,
        thumbnailPaths: many.length > 0 ? many : single ? [single] : [],
      }
    }
  }

  return null
}

/** Up to three distinct thumbnails, in order. */
export function referenceThumbnails(ref: MessageReference): string[] {
  return paths(ref.thumbnailPaths).slice(0, 3)
}

export function referenceTitle(ref: MessageReference): string {
  const title = text(ref.displayTitle)
  if (title) return title
  if (isBundleReference(ref)) return isPickupReference(ref) ? 'Pickup order bundle' : 'Order bundle'
  if (isOrderReference(ref)) return isPickupReference(ref) ? 'Pickup order' : 'Shipping order'
  return 'Listing'
}

/** Small label above the title: what kind of thing the card is. */
export function referenceEyebrow(ref: MessageReference): string {
  if (isListingReference(ref)) return 'Listing'
  if (isBundleReference(ref)) return isPickupReference(ref) ? 'Pickup bundle' : 'Shipping bundle'
  return isPickupReference(ref) ? 'Pickup order' : 'Shipping order'
}

function money(amount: number): string {
  return `$${amount.toFixed(2)}`
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`
}

/**
 * The card's order or listing label. An order shows its complete number when
 * the server attached one; older messages saved only a shortened reference
 * ("Order #AGVR"), which is shown as saved when no number came with it.
 */
export function referenceLabel(ref: MessageReference): string | undefined {
  const orderNumber = isOrderReference(ref) ? text(ref.orderNumber) : undefined
  return orderNumber ? `Order ${orderNumber}` : text(ref.reference)
}

/**
 * Facts about the item, one per entry, so the card can lay them out without
 * separators: order number or listing type, fulfillment, status.
 */
export function referenceFacts(ref: MessageReference): string[] {
  return facts(ref, referenceLabel(ref))
}

function facts(ref: MessageReference, label: string | undefined): string[] {
  if (isBundleReference(ref)) {
    return [
      plural(ref.orderCount ?? 1, 'order', 'orders'),
      plural(ref.itemCount ?? 1, 'item', 'items'),
    ]
  }
  const facts: string[] = []
  if (label) facts.push(label)
  if (isOrderReference(ref)) facts.push(isPickupReference(ref) ? 'Pickup' : 'Shipping')
  const status = text(ref.status)?.replace(/_/g, ' ')
  if (status) facts.push(status)
  return facts
}

/** Money facts, one per entry: total, then shipping when charged. */
export function referencePriceFacts(ref: MessageReference): string[] {
  const facts: string[] = []
  const total = num(ref.totalAmount)
  if (total !== undefined && total > 0) {
    facts.push(isListingReference(ref) ? money(total) : `${money(total)} total`)
  }
  const shipping = num(ref.shippingAmount)
  if (shipping !== undefined && shipping > 0) facts.push(`${money(shipping)} shipping`)
  return facts
}

/**
 * The plain-text stand-in that prefixes a message body on the wire. Must match
 * the mobile app's `SharedMessageContext.summary` byte for byte, because every
 * client strips exactly this string back out of the body. It is built from the
 * saved `reference`, never the server-attached `orderNumber`, so messages
 * saved before numbers were attached still strip.
 */
export function referenceSummary(ref: MessageReference): string {
  const parts = [referenceTitle(ref)]
  const detail = facts(ref, text(ref.reference)).join(' · ')
  if (detail) parts.push(detail)
  const price = referencePriceFacts(ref).join(' · ')
  if (price) parts.push(price)
  return parts.join(' · ')
}

/** Body text as typed, with the wire summary of `ref` removed. */
export function stripReferenceSummary(
  body: string | null | undefined,
  ref: MessageReference | null
): string {
  const raw = body ?? ''
  if (!ref) return raw
  const summary = referenceSummary(ref)
  if (raw === summary) return ''
  const prefix = `${summary}\n\n`
  return raw.startsWith(prefix) ? raw.slice(prefix.length) : raw
}

/** Wire body for a message sent with a card attached. */
export function bodyWithReferenceSummary(body: string, ref: MessageReference): string {
  const trimmed = body.trim()
  const summary = referenceSummary(ref)
  return trimmed ? `${summary}\n\n${trimmed}` : summary
}

/** Inbox preview for a message that is only a card. */
export function referencePreview(ref: MessageReference): string {
  if (isListingReference(ref)) return 'Shared a listing'
  if (isBundleReference(ref)) return 'Shared an order bundle'
  return 'Shared an order'
}

/**
 * Where a card opens. Listings open the listing page. Orders open the viewer's
 * own orders surface filtered to that order, since buyers and sellers manage
 * orders in different places on the web. Bundles have no page of their own.
 */
export function referenceHref(
  ref: MessageReference,
  opts: { lang: string; viewerIsSeller: boolean }
): string | null {
  const lang = opts.lang || 'en'
  if (isListingReference(ref)) {
    return `/${lang}/auction/${ref.listingId || ref.entityId}`
  }
  if (isOrderReference(ref)) {
    const search = encodeURIComponent(
      text(ref.orderNumber) || text(ref.reference)?.replace(/^Order #?/, '') || ref.entityId
    )
    return opts.viewerIsSeller
      ? `/${lang}/seller/orders?search=${search}`
      : `/${lang}/profile?tab=orders&search=${search}`
  }
  return null
}
